import { supabase } from '../lib/supabase';
import { contractValidation } from '../utils/validationUtils';

export const buyerRepresentationContractService = {
  // Calculate retainer amounts based on percentages
  calculateRetainerAmounts(contractAmount, arddjoPercentage, clientPercentage) {
    const arddjoAmount = (contractAmount * arddjoPercentage) / 100;
    const clientAmount = (contractAmount * clientPercentage) / 100;
    return {
      arddjoRetainerAmount: arddjoAmount,
      clientRetainerAmount: clientAmount,
      totalRetainerAmount: arddjoAmount + clientAmount
    };
  },

  // Create buyer representation contract
  async createBuyerRepresentationContract(contractData) {
    try {
      // SERVER-SIDE VALIDATION
      const validation = contractValidation?.validateBuyerContract(contractData);
      if (!validation?.isValid) {
        const errorMessages = Object.values(validation?.errors)?.join('، ');
        throw new Error(`خطأ في البيانات: ${errorMessages}`);
      }

      const { data, error } = await supabase?.from('buyer_representation_contracts')?.insert({
        contract_id: contractData?.contractId,
        arddjo_retainer_percentage: contractData?.arddjoRetainerPercentage,
        client_retainer_percentage: contractData?.clientRetainerPercentage,
        representation_duration_months: contractData?.representationDurationMonths || 6,
        exclusive_representation: contractData?.exclusiveRepresentation ?? true,
        property_types: contractData?.propertyTypes || [],
        budget_min: contractData?.budgetMin,
        budget_max: contractData?.budgetMax,
        preferred_locations: contractData?.preferredLocations || [],
        notes: contractData?.notes
      })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating buyer representation contract:', error);
      throw error;
    }
  },

  // Get buyer representation contract by contract ID
  async getBuyerRepresentationContract(contractId) {
    try {
      const { data, error } = await supabase?.from('buyer_representation_contracts')?.select(`
          *,
          contract:contracts(
            contract_number,
            contract_type,
            amount,
            status
          ),
          arddjo_accepted_by_profile:user_profiles!buyer_representation_contracts_arddjo_accepted_by_fkey(
            full_name,
            email
          ),
          client_accepted_by_profile:user_profiles!buyer_representation_contracts_client_accepted_by_fkey(
            full_name,
            email
          )
        `)?.eq('contract_id', contractId)?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching buyer representation contract:', error);
      throw error;
    }
  },

  // Update buyer representation contract
  async updateBuyerRepresentationContract(buyerContractId, updates) {
    try {
      const { data, error } = await supabase?.from('buyer_representation_contracts')?.update({
        arddjo_retainer_percentage: updates?.arddjoRetainerPercentage,
        client_retainer_percentage: updates?.clientRetainerPercentage,
        representation_duration_months: updates?.representationDurationMonths,
        exclusive_representation: updates?.exclusiveRepresentation,
        property_types: updates?.propertyTypes,
        budget_min: updates?.budgetMin,
        budget_max: updates?.budgetMax,
        preferred_locations: updates?.preferredLocations,
        notes: updates?.notes,
        updated_at: new Date()?.toISOString()
      })?.eq('id', buyerContractId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating buyer representation contract:', error);
      throw error;
    }
  },

  // ARDDJO acceptance
  async arddjoAcceptance(buyerContractId, status) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('buyer_representation_contracts')?.update({
        arddjo_acceptance_status: status,
        arddjo_accepted_by: status === 'accepted' ? user?.id : null,
        arddjo_accepted_at: status === 'accepted' ? new Date()?.toISOString() : null
      })?.eq('id', buyerContractId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating ARDDJO acceptance:', error);
      throw error;
    }
  },

  // Client acceptance
  async clientAcceptance(buyerContractId, status) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('buyer_representation_contracts')?.update({
        client_acceptance_status: status,
        client_accepted_by: status === 'accepted' ? user?.id : null,
        client_accepted_at: status === 'accepted' ? new Date()?.toISOString() : null
      })?.eq('id', buyerContractId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating client acceptance:', error);
      throw error;
    }
  },

  // Get all buyer representation contracts with filters
  async getBuyerRepresentationContracts(filters = {}) {
    try {
      let query = supabase?.from('buyer_representation_contracts')?.select(`
          *,
          contract:contracts(
            contract_number,
            contract_type,
            amount,
            status,
            client:clients(
              name,
              email,
              phone
            )
          )
        `);

      if (filters?.arddjoAcceptanceStatus) {
        query = query?.eq('arddjo_acceptance_status', filters?.arddjoAcceptanceStatus);
      }

      if (filters?.clientAcceptanceStatus) {
        query = query?.eq('client_acceptance_status', filters?.clientAcceptanceStatus);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching buyer representation contracts:', error);
      throw error;
    }
  },

  // Real-time subscription for buyer representation contract updates
  subscribeToBuyerRepresentationContract(contractId, callback) {
    const channel = supabase?.channel(`buyer-representation-contract-${contractId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'buyer_representation_contracts',
          filter: `contract_id=eq.${contractId}`
        },
        callback
      )?.subscribe();

    return channel;
  },

  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};